import { type UseQueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';

import { api } from './api';
import { useAuthStore } from './stores/auth-store';

const PATH = Symbol('PATH');
const ARGS = Symbol('ARGS');

const createRecorder = (path: string[] = [], args: unknown[] = []): unknown => {
  const fn = () => undefined;
  Object.assign(fn, { [PATH]: path, [ARGS]: args });
  return new Proxy(fn, {
    get: (_target, property) => {
      if (property === PATH) return path;
      if (property === ARGS) return args;
      if (property === 'then') return undefined;
      return createRecorder([...path, String(property)], args);
    },
    apply: (_target, _this, callArguments) => createRecorder(path, [...args, ...callArguments]),
  });
};

function pathAndArguments(selector: (client: never) => unknown) {
  const result = selector(createRecorder() as never) as Record<symbol, unknown>;
  return {
    path: (result[PATH] as string[] | undefined) ?? [],
    args: (result[ARGS] as unknown[] | undefined) ?? [],
  };
}

type TreatyResponse<R> = Promise<{ data: R | null; error: unknown; status: number }>;

export const createTreatyHooks = <T extends object>(getClient: () => T) => ({
  useQuery: <R>(
    selector: (client: T) => TreatyResponse<R>,
    options?: Omit<UseQueryOptions<R, Error>, 'queryKey' | 'queryFn'>,
  ): UseQueryResult<R, Error> => {
    const recorded = pathAndArguments(selector as (client: never) => unknown);
    return useQuery({
      queryKey: [...recorded.path, ...recorded.args],
      queryFn: async () => {
        const response = await selector(getClient());
        if (response.status === 401) {
          api.clearToken();
          useAuthStore.getState().logout();
        }
        if (response.error || response.data === null) {
          const message =
            typeof response.error === 'object' &&
            response.error !== null &&
            'value' in response.error &&
            typeof response.error.value === 'object' &&
            response.error.value !== null &&
            'message' in response.error.value
              ? String(response.error.value.message)
              : 'Request failed';
          throw new Error(message);
        }
        return response.data;
      },
      ...options,
    });
  },
});
